<template>
  <div class="h-screen overflow-hidden flex flex-col bg-white border-solid border-l border-0 border-gray-border">
    <div class="border-solid border-b border-0 border-gray-border px-8 pb-8">
      <input-with-error-message v-if="isRenameMode"
                                id="market-editor-name-input"
                                :value.sync="marketName"
                                :is-loading="isLoading"
                                class="h-full mt-5 w-full"
                                @escape="cancelRenameMode"
                                @submit="submitMarketName"
      />
      <div v-else class="mt-8 inline-block pr-12 w-full relative">
        <h3 class="font-semibold truncate">
          <span class="cursor-pointer text-gray-dark hover:text-primary-focus hover:underline"
                @click="$emit('display-bot-summary')"
          >{{ groupBotName }}</span>
          <span class="text-gray ml-1">/</span>
          <span class="text-black ml-1">{{ botDisplayName }}</span>
        </h3>
        <side-list-menu :items="menuItems"
                        class="absolute top-0 right-0 -mt-1"
                        @rename="setRenameMode(true)"
                        @delete="$emit('request-delete-market', bot)"
        />
      </div>
    </div>

    <div class="mt-8 px-8 flex flex-col gap-6 text-gray-dark">
      <div>
        <label class="font-bold">Market Type</label>
        <div class="mt-1 flex items-center gap-2">
          <span>{{ bot.is_primary_variant ? 'Primary' : 'Secondary' }}</span>
          <vs-icon icon="storefront"/>
        </div>
      </div>
      <div>
        <label class="font-bold">Language</label>
        <div class="mt-1 flex items-center gap-2">
          <span>{{ marketLanguage }}</span>
          <vs-icon icon="language"/>
        </div>
      </div>

      <div>
        <label class="font-bold">Timezone</label>
        <div class="mt-1 flex items-center gap-2">
          <span>{{ bot.timezone || '-' }}</span>
          <vs-icon icon="schedule"/>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import InputWithErrorMessage from '@/components/common/InputWithErrorMessage.vue'
import SideListMenu from '@/components/common/SideListMenu'
import botModule from '@/store/modules/bot.module'
import { MENU_ITEMS } from '@/utils/editor.constants'
import { BOT_LANGUAGES } from '@/utils/bot.constants'

export default {
  name: 'SettingsBotMarketEditor',

  components: {
    InputWithErrorMessage,
    SideListMenu
  },

  props: {
    bot: {
      type: Object,
      required: true
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    renameTrigger: {
      type: Number,
      default: 0
    },
    multiBots: {
      type: Array,
      default: () => []
    }
  },

  emits: ['rename-market', 'display-bot-summary', 'rename-trigger-consumed', 'request-delete-market'],

  data() {
    return {
      marketName: '',
      isRenameMode: false
    }
  },

  watch: {
    bot: {
      immediate: true,
      handler(bot, oldBot) {
        const idChanged = !oldBot || oldBot.id !== bot.id
        if (idChanged) {
          this.marketName = bot && (bot.multi_bot_name || bot.multiBotName || bot.name) ? (bot.multi_bot_name || bot.multiBotName || bot.name) : ''
          this.isRenameMode = false
        } else if (bot && !this.isRenameMode) {
          this.marketName = bot.multi_bot_name || bot.multiBotName || bot.name
        }
      }
    },
    renameTrigger: {
      immediate: true,
      handler(val, oldVal) {
        if (val > 0 && val !== oldVal) {
          this.$nextTick(() => {
            this.setRenameMode(true)
            this.$emit('rename-trigger-consumed')
          })
        }
      }
    }
  },

  computed: {
    menuItems() {
      return ['111', '222'].includes(this.bot.id) ?
        [MENU_ITEMS.rename] :
        [MENU_ITEMS.rename, MENU_ITEMS.delete]
    },
    groupBotName() {
      return botModule.resolvePrimaryBot(this.bot.id)?.name || this.bot.name
    },
    botDisplayName() {
      return this.bot.multi_bot_name || this.bot.multiBotName || this.bot.name
    },
    marketLanguage() {
      const language = BOT_LANGUAGES.find(item => item.value === this.bot.language)
      return language ? language.text : '-'
    }
  },

  methods: {
    setRenameMode(isRenameMode) {
      this.isRenameMode = isRenameMode
      if (!isRenameMode) this.marketName = this.bot.multi_bot_name || this.bot.multiBotName || this.bot.name
    },
    cancelRenameMode() {
      this.marketName = this.bot.multi_bot_name || this.bot.multiBotName || this.bot.name
      this.isRenameMode = false
    },
    submitMarketName(name) {
      const currentMarketName = this.bot.multi_bot_name || this.bot.multiBotName || this.bot.name
      if (!name || name === currentMarketName) {
        this.isRenameMode = false
        return
      }
      this.$emit('rename-market', { ...this.bot, name })
      this.isRenameMode = false
    }
  }
}
</script>
